'use client';

import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface DataPoint {
  name: string;
  [key: string]: string | number;
}

interface MetricAreaChartProps {
  data: DataPoint[];
  areas: Array<{
    dataKey: string;
    name: string;
    color?: string;
  }>;
  xAxisLabel?: string;
  yAxisLabel?: string;
  height?: number;
  stacked?: boolean;
}

const BRAND_COLORS = {
  navy: '#0A1E2E',
  orange: '#E89841',
  cream: '#E8D5B7',
  blue: '#4A90E2',
  green: '#6FCF97',
  yellow: '#F2C94C',
  red: '#EB5757',
};

export default function MetricAreaChart({
  data,
  areas,
  xAxisLabel,
  yAxisLabel,
  height = 300,
  stacked = true,
}: MetricAreaChartProps) {
  const defaultColors = [
    BRAND_COLORS.orange,
    BRAND_COLORS.blue,
    BRAND_COLORS.green,
    BRAND_COLORS.yellow,
    BRAND_COLORS.red,
  ];

  return (
    <div className="w-full my-6 p-4 bg-background/40 border border-border rounded-lg">
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={BRAND_COLORS.cream + '20'} />
          <XAxis
            dataKey="name"
            stroke={BRAND_COLORS.cream}
            tick={{ fill: BRAND_COLORS.cream }}
            label={xAxisLabel ? { value: xAxisLabel, position: 'insideBottom', offset: -5, fill: BRAND_COLORS.cream } : undefined}
          />
          <YAxis
            stroke={BRAND_COLORS.cream}
            tick={{ fill: BRAND_COLORS.cream }}
            label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'insideLeft', fill: BRAND_COLORS.cream } : undefined}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: BRAND_COLORS.navy,
              border: `1px solid ${BRAND_COLORS.orange}40`,
              borderRadius: '8px',
              color: BRAND_COLORS.cream,
            }}
            labelStyle={{ color: BRAND_COLORS.orange }}
          />
          <Legend
            wrapperStyle={{ color: BRAND_COLORS.cream }}
          />
          {areas.map((area, index) => (
            <Area
              key={area.dataKey}
              type="monotone"
              dataKey={area.dataKey}
              name={area.name}
              stackId={stacked ? 'stack' : undefined}
              stroke={area.color || defaultColors[index % defaultColors.length]}
              fill={area.color || defaultColors[index % defaultColors.length]}
              fillOpacity={0.6}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
